/**
 * Модельная таблица смертности, откалиброванная по ожидаемой продолжительности
 * жизни при рождении (e0), детской (u5mr) и младенческой (imr) смертности.
 *
 * Модель:
 * - q0 = imr / 1000;
 * - q1..q4 — равные доли от (u5mr − imr) / 1000;
 * - с 5 лет — Гомпертц–Мейкем: qx = 1 − exp(−(a + b·e^{c·x})),
 *   c = 0.095, a = 0.0003, b подбирается бинарным поиском так, чтобы
 *   expectedLifespan совпала с e0 (±0.05);
 * - qx(age ≥ MAX_AGE) = 1 — никто не живёт дольше 110 лет.
 *
 * Чистая функция: ни случайности, ни данных снаружи.
 */

export interface LifeTable {
  /** Вероятность умереть в течение года в возрасте `age` (целые годы, [0, 1]). */
  qx(age: number): number;
}

/** Возраст, в котором qx = 1: жизнь гарантированно заканчивается. */
export const MAX_AGE = 110;

/** Параметры Гомпертца–Мейкема, фиксированные по спецификации плана. */
const GOMPERTZ_C = 0.095;
const MAKEHAM_A = 0.0003;

/** Границы бинарного поиска по b (в них укладываются e0 от ~35 до ~95 лет). */
const B_LOW = 1e-9;
const B_HIGH = 1;
const TARGET_TOLERANCE = 0.05;
const MAX_ITERATIONS = 80;

/** Возраст, с которого действует формула Гомпертца–Мейкема. */
const ADULT_AGE = 5;

function clamp01(x: number): number {
  if (!(x > 0)) return 0;
  return x < 1 ? x : 1;
}

/** Нормализует возраст к целому в [0, MAX_AGE]. */
function normalizeAge(age: number): number {
  if (!Number.isFinite(age) || age < 0) return 0;
  const whole = Math.floor(age);
  return whole > MAX_AGE ? MAX_AGE : whole;
}

/** Массив qx[0..MAX_AGE] для заданного b. */
function buildQx(u5mr: number, imr: number, b: number): Float64Array {
  const qx = new Float64Array(MAX_AGE + 1);
  const q0 = clamp01(imr / 1000);
  const childShare = clamp01((u5mr - imr) / 1000 / (ADULT_AGE - 1));
  qx[0] = q0;
  for (let age = 1; age < ADULT_AGE; age += 1) qx[age] = childShare;
  for (let age = ADULT_AGE; age < MAX_AGE; age += 1) {
    const hazard = MAKEHAM_A + b * Math.exp(GOMPERTZ_C * age);
    qx[age] = clamp01(1 - Math.exp(-hazard));
  }
  qx[MAX_AGE] = 1;
  return qx;
}

/** Ожидаемая продолжительность жизни при рождении: Σ дожития по годам (умершие в году считаются за полгода). */
function lifespanOf(qx: Float64Array): number {
  let alive = 1;
  let total = 0;
  for (let age = 0; age <= MAX_AGE; age += 1) {
    const q = qx[age]!;
    total += alive * (1 - q / 2);
    alive *= 1 - q;
    if (alive <= 0) break;
  }
  return total;
}

function tableOf(qx: Float64Array): LifeTable {
  return {
    qx(age: number): number {
      return qx[normalizeAge(age)]!;
    },
  };
}

/**
 * Строит таблицу смертности так, чтобы expectedLifespan(lt) ≈ e0.
 * @param e0 ожидаемая продолжительность жизни при рождении, лет
 * @param u5mr смертность до 5 лет, на 1000 рождённых
 * @param imr младенческая смертность, на 1000 рождённых
 */
export function buildLifeTable(e0: number, u5mr: number, imr: number): LifeTable {
  let lo = B_LOW;
  let hi = B_HIGH;
  let best = buildQx(u5mr, imr, Math.sqrt(lo * hi));
  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    // Поиск в логарифмической шкале: b меняется на несколько порядков.
    const mid = Math.sqrt(lo * hi);
    best = buildQx(u5mr, imr, mid);
    const diff = lifespanOf(best) - e0;
    if (Math.abs(diff) <= TARGET_TOLERANCE) break;
    // Больше b → выше смертность → короче жизнь.
    if (diff > 0) lo = mid;
    else hi = mid;
  }
  return tableOf(best);
}

/** Ожидаемая продолжительность жизни при рождении по таблице — для калибровки и отчётов. */
export function expectedLifespan(lt: LifeTable): number {
  const qx = new Float64Array(MAX_AGE + 1);
  for (let age = 0; age <= MAX_AGE; age += 1) qx[age] = clamp01(lt.qx(age));
  return lifespanOf(qx);
}
